/**
 * Utility functions to extract longitude and latitude from Google Maps URLs
 */

export interface ExtractedCoordinates {
  latitude: number | null;
  longitude: number | null;
  success: boolean;
  error?: string;
  resolvedUrl?: string;
  method?: string;
  methodsTried?: string[];
}

/**
 * Resolve shortened Google Maps URLs to get the full URL
 * This function follows redirects to get the final destination URL
 */
export const resolveShortenedUrl = async (shortUrl: string): Promise<{ success: boolean; fullUrl?: string; error?: string }> => {
  try {
    // Check if it's a shortened URL
    if (!shortUrl.includes('goo.gl') && !shortUrl.includes('maps.app.goo.gl')) {
      return { success: true, fullUrl: shortUrl };
    }

    // Make a HEAD request to follow redirects
    const response = await fetch(shortUrl, {
      method: 'HEAD',
      redirect: 'follow',
      mode: 'cors'
    });

    if (response.ok) {
      const fullUrl = response.url;
      return { success: true, fullUrl };
    } else {
      return { 
        success: false, 
        error: `Failed to resolve URL: HTTP ${response.status}` 
      };
    }
  } catch (error) {
    // If CORS fails, try a different approach
    try {
      // Alternative: Use a proxy service or try with different headers
      const response = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(shortUrl)}`);
      const data = await response.json();
      
      if (data.contents) {
        // Extract the final URL from the response
        const urlMatch = data.contents.match(/href="([^"]*maps\.google[^"]*)"/);
        if (urlMatch) {
          return { success: true, fullUrl: urlMatch[1] };
        }
      }
      
      return { 
        success: false, 
        error: 'Failed to resolve shortened URL' 
      };
    } catch (proxyError) {
      return { 
        success: false, 
        error: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      };
    }
  }
};

/**
 * Extract coordinates from Google Maps URL
 * For full URLs: Extract directly in frontend
 * For short URLs: Call backend API to resolve first
 */
export const extractCoordinatesFromGoogleMapsUrl = async (url: string): Promise<ExtractedCoordinates> => {
  try {
    // Clean the URL
    const cleanUrl = url.trim();
    
    if (!cleanUrl) {
      return {
        latitude: null,
        longitude: null,
        success: false,
        error: 'URL is empty'
      };
    }

    // Check if it's a shortened URL that needs backend resolution
    const isShortUrl = cleanUrl.includes('goo.gl') || cleanUrl.includes('maps.app.goo.gl');
    
    if (isShortUrl) {
      // For short URLs, call backend API to resolve
      const backendUrl = `${process.env.REACT_APP_BASE_URL || 'http://localhost:3030'}/url-resolver`;
      const response = await fetch(`${backendUrl}?url=${encodeURIComponent(cleanUrl)}`);
      
      if (!response.ok) {
        throw new Error(`Backend request failed: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.coordinates) {
        return {
          latitude: data.coordinates.latitude,
          longitude: data.coordinates.longitude,
          success: true,
          resolvedUrl: data.resolvedUrl,
          method: data.method
        };
      } else {
        return {
          latitude: null,
          longitude: null,
          success: false,
          error: data.error || 'Failed to resolve URL',
          resolvedUrl: data.resolvedUrl,
          methodsTried: data.methods_tried
        };
      }
    } else {
      // For full URLs, extract coordinates directly in frontend
      const coordinates = extractCoordinatesFromFullUrl(cleanUrl);
      
      if (coordinates.success) {
        return {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          success: true,
          method: 'frontend_direct'
        };
      } else {
        return {
          latitude: null,
          longitude: null,
          success: false,
          error: coordinates.error || 'No coordinates found in URL'
        };
      }
    }

  } catch (error) {
    return {
      latitude: null,
      longitude: null,
      success: false,
      error: `Error parsing URL: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Fallback function to extract coordinates from full URLs (not shortened)
 * This is used when the backend service is unavailable
 */
const extractCoordinatesFromFullUrl = (url: string): ExtractedCoordinates => {
  // Method 1: Extract from @lat,lng format (most common)
  const atPattern = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
  const atMatch = url.match(atPattern);
  if (atMatch) {
    const latitude = parseFloat(atMatch[1]);
    const longitude = parseFloat(atMatch[2]);
    
    if (isValidCoordinate(latitude, longitude)) {
      return {
        latitude,
        longitude,
        success: true
      };
    }
  }

  // Method 2: Extract from q=lat,lng format
  const qPattern = /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
  const qMatch = url.match(qPattern);
  if (qMatch) {
    const latitude = parseFloat(qMatch[1]);
    const longitude = parseFloat(qMatch[2]);
    
    if (isValidCoordinate(latitude, longitude)) {
      return {
        latitude,
        longitude,
        success: true
      };
    }
  }

  // Method 3: Extract from ll=lat,lng format
  const llPattern = /[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
  const llMatch = url.match(llPattern);
  if (llMatch) {
    const latitude = parseFloat(llMatch[1]);
    const longitude = parseFloat(llMatch[2]);
    
    if (isValidCoordinate(latitude, longitude)) {
      return {
        latitude,
        longitude,
        success: true
      };
    }
  }

  // Method 4: Extract from center=lat,lng format
  const centerPattern = /[?&]center=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
  const centerMatch = url.match(centerPattern);
  if (centerMatch) {
    const latitude = parseFloat(centerMatch[1]);
    const longitude = parseFloat(centerMatch[2]);
    
    if (isValidCoordinate(latitude, longitude)) {
      return {
        latitude,
        longitude,
        success: true
      };
    }
  }

  return {
    latitude: null,
    longitude: null,
    success: false,
    error: 'No valid coordinates found in URL'
  };
};

/**
 * Validate if coordinates are within valid ranges
 */
const isValidCoordinate = (latitude: number, longitude: number): boolean => {
  return (
    !isNaN(latitude) &&
    !isNaN(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};


